import React, { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';

import type { FeatureFlagName, FeatureFlagsMap } from './flags';
import { FEATURE_FLAGS } from './flags';

type FeatureFlagContextValue = {
	flags: FeatureFlagsMap;
	loading: boolean;
};

const FeatureFlagContext = createContext<FeatureFlagContextValue | undefined>(undefined);

type FeatureFlagProviderProps = {
	children: ReactNode;
	overrides?: Partial<Record<FeatureFlagName, boolean>>;
	/** RBAC-granted features from usePermissionMap().grantedFeatures('scout') */
	grantedFeatures?: string[];
	loading?: boolean;
};

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({
	children,
	overrides,
	grantedFeatures,
	loading,
}) => {
	const value = useMemo<FeatureFlagContextValue>(() => {
		const flags = { ...FEATURE_FLAGS };
		// RBAC layer: granted features from user_permissions table
		if (grantedFeatures) {
			for (const feat of grantedFeatures) {
				if (feat in flags) (flags as Record<string, boolean>)[feat] = true;
			}
		}
		// Explicit overrides (props)
		if (overrides) Object.assign(flags, overrides);
		return { flags, loading: loading ?? false };
	}, [overrides, grantedFeatures, loading]);

	return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
};

export function useFeatureFlags(): FeatureFlagContextValue {
	const context = useContext(FeatureFlagContext);

	if (!context) {
		throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
	}

	return context;
}

